import { error, log, sys } from 'cc';
import Game from '../../Game';
import Network, { RequestDataType, RequestSetting, ResponseDataType } from './Network';
import ErrorName from '../../ErrorHandler/ErrorName';
import Delay from '../../Toolkit/Utils/Delay';

type MessageDispatcher = {
    [path: string]: [(data: any) => void, (reason?: any) => void][];
};

enum RejectReason {
    CANCELLED_BY_RECONNECT = 'CANCELLED_BY_RECONNECT',
}

/** Native websocket with additional functionalities
 * 1. type support
 * 2. event-based
 * 3. timeout tracking
 * */
export default class NetworkWebSocket extends Network {
    private _websocket: WebSocket = null;
    private readonly defaultMessageDispatcher: MessageDispatcher = {};
    private readonly subjectiveMessageDispatcherMap: { [subject: string]: MessageDispatcher } = {};

    public get readyState() {
        return this._websocket.readyState;
    }

    public get isClosed() {
        return !this._websocket || this._websocket.readyState === WebSocket.CLOSED || this._websocket.readyState === WebSocket.CLOSING;
    }

    public open() {
        try {
            this.emit(Network.EVENT_TYPE.START_CONNTECT);
            this._websocket = new WebSocket(this.url);
        } catch (err) {
            error(`WebSocket connection error in open: ${err}`);
            this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED);
            return Delay.forever();
        }
        const connectionPromise = new Promise<void>((resolve) => {
            this._websocket.onopen = () => {
                log('WebSocket connection start successfully');
                this.emit(Network.EVENT_TYPE.CONNECTED);
                resolve();
            };
        });
        this._websocket.onmessage = (event: MessageEvent) => {
            const receiveData = JSON.parse(event.data);
            const { Path, Data, Err, Log, ErrorCode, Message, Subject }: ResponseDataType =
                receiveData instanceof Object ? receiveData : JSON.parse(this.cryptor.decrypt(receiveData));
            const errorKey = Err ?? ErrorCode?.toString();
            if (Message) {
                log(`WebSocket Server message at {${Path}}:`, Message);
            }
            if (Log) {
                log(`WebSocket Server log at {${Path}}:`, Log);
            }
            const dispatchers: MessageDispatcher[] = [this.defaultMessageDispatcher];
            // if subjecy is specified, dispatch to the subjective dispatcher first
            if (Subject) {
                dispatchers.unshift(this.subjectiveMessageDispatcherMap[Subject] || (this.subjectiveMessageDispatcherMap[Subject] = {}));
            }

            if (errorKey) {
                // broadcast data with error
                if (Data && Subject) {
                    Data.Subject = Subject;
                }

                for (let i = 0; i < dispatchers.length; i++) {
                    const dispatcher = dispatchers[i];
                    const reject: (reason?: any) => void = dispatcher[Path]?.shift()?.[1];
                    if (reject) {
                        reject(errorKey);
                        return;
                    }
                    if (this.hasEventListener(`${Path}_error`)) {
                        // server send error initiatively, so no reject function
                        this.emit(`${Path}_error`, errorKey, Subject, Data);
                        return;
                    }
                }
                this.emit(Network.EVENT_TYPE.ERROR, errorKey, Subject, Data, Path);
                return;
            }

            if (Data && Subject) {
                Data.Subject = Subject;
            }
            // response
            for (let i = 0; i < dispatchers.length; i++) {
                const dispatcher = dispatchers[i];
                const dispatch = dispatcher[Path]?.shift()?.[0];
                if (dispatch) {
                    dispatch(Data);
                    break;
                }
            }
            // boardcast
            if (this.hasEventListener(Path as string)) {
                this.emit(Path as string, Data);
            }
        };
        this._websocket.onerror = () => {
            if (sys.os === sys.OS.IOS || sys.os === sys.OS.OSX) {
                // iOS 16+ triggers onerror when the WebSocket connection is closed, so ignore it.
                return;
            }
            this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED);
            log(`WebSocket connection error in onerror: ${this.url}`);
        };
        this._websocket.onclose = async () => {
            log('WebSocket connection closed');
            this.emit(Network.EVENT_TYPE.DISCONNECTED);
        };
        return connectionPromise;
    }

    public async send(path: string, postData: any, setting: RequestSetting, subject?: string): Promise<any> {
        const { expectedErrors, handleAllErrors, timeout } = setting;
        if (timeout < 0 && timeout !== -1) {
            throw new Error('Invalid timeout');
        }
        // 連線狀態為開啟才能傳送訊息
        if (this._websocket.readyState !== WebSocket.OPEN) {
            this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED);
            log(`WebSocket is not open. readyState=${this._websocket.readyState}`);
            return Delay.forever();
        }
        const requestData: RequestDataType = Object.assign(postData ? { Data: postData } : {}, { Path: path, Subject: subject });
        const encryptResult = Game.config.needEncrypt ? this.cryptor.encrypt(JSON.stringify(requestData)) : requestData;
        this._websocket.send(JSON.stringify(encryptResult));
        if (setting.noReply) {
            return Delay.forever();
        }
        return Promise.race([
            new Promise<any>((resolve, reject) => {
                const dispatcher = subject
                    ? this.subjectiveMessageDispatcherMap[subject] || (this.subjectiveMessageDispatcherMap[subject] = {})
                    : this.defaultMessageDispatcher;
                const dispatchPath = dispatcher[path] || (dispatcher[path] = []);
                dispatchPath.push([resolve, reject]);
            }),
            timeout === -1 ? Delay.forever() : Delay.reject<any>(timeout, ErrorName.TIMEOUT, true),
        ]).catch((reason) => {
            if (reason === RejectReason.CANCELLED_BY_RECONNECT) {
                return Delay.forever();
            }
            if (handleAllErrors || expectedErrors.includes(reason)) {
                throw reason;
            }
            if (reason === ErrorName.TIMEOUT) {
                if (this.isClosed) {
                    return Delay.forever();
                }
                error(`WebSocket timeout at path ${path as string}`);
                this.emit(Network.EVENT_TYPE.ERROR, ErrorName.TIMEOUT, subject, requestData);
            } else {
                this.emit(Network.EVENT_TYPE.ERROR, reason, subject, requestData);
            }
            return Delay.forever();
        });
    }

    public close() {
        if (this.isClosed) {
            return;
        }
        this._websocket.close();
    }

    public async reconnect(): Promise<void> {
        this.emit(Network.EVENT_TYPE.START_RECONNECT);
        const disconnectPromise = this.isClosed ? Promise.resolve() : Delay.event(Network.EVENT_TYPE.DISCONNECTED, this);
        this._clearMessageDispatcher();
        this.close();
        await disconnectPromise;
        await this.open();
        this.emit(Network.EVENT_TYPE.RECONNECTED);
    }

    private _clearMessageDispatcher() {
        this._cancelPendingRequests(this.defaultMessageDispatcher, RejectReason.CANCELLED_BY_RECONNECT);
        Object.keys(this.subjectiveMessageDispatcherMap).forEach((subject) => {
            this._cancelPendingRequests(this.subjectiveMessageDispatcherMap[subject], RejectReason.CANCELLED_BY_RECONNECT);
            delete this.subjectiveMessageDispatcherMap[subject];
        });
    }

    private _cancelPendingRequests(dispatcher: MessageDispatcher, reason: string) {
        Object.entries(dispatcher).forEach(([path, pendingList]) => {
            pendingList.forEach(([, reject]) => reject(reason));
            delete dispatcher[path];
        });
    }
}
